import React from "react";
import { Filter, Truck, Clock, Star, ChevronRight } from "lucide-react";

const CategorySidebar = ({
  categories,
  selectedCategory,
  handleCategoryChange,
  restaurantMenu,
}) => {
  return (
    <>
      {/* Mobile Horizontal Scroll View */}
      <div className="lg:hidden w-full sticky top-0 z-30 bg-white shadow-sm">
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Filter className="w-3 h-3 text-white" />
              </div>
              <span className="font-semibold text-gray-900 text-sm truncate">
                Menu Categories
              </span>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-white/80 rounded-full flex-shrink-0">
              <Star className="w-3 h-3 text-amber-500 fill-current" />
              <span className="text-xs font-medium text-gray-700">4.8</span>
            </div>
          </div>
        </div>

        {/* Horizontal Scrolling Categories - Fixed container */}
        <div className="w-full overflow-hidden">
          <div className="overflow-x-auto scrollbar-hide py-2 px-3">
            <div
              className="flex gap-2 flex-nowrap"
              style={{ width: "fit-content", maxWidth: "calc(100vw - 24px)" }}
            >
              {categories.length > 0 ? (
                categories.map((category) => {
                  // restaurantMenu is now a flat array, not an object
                  const categoryItems = Array.isArray(restaurantMenu)
                    ? restaurantMenu.filter(
                        (item) => item.category === category
                      )
                    : [];
                  const categoryEmoji = {
                    appetizers: "🥗",
                    mains: "🍖",
                    desserts: "🍰",
                    beverages: "🥤",
                    pasta: "🍝",
                    pizza: "🍕",
                    sushi: "🍱",
                    ramen: "🍜",
                    salads: "🥙",
                    soups: "🍲",
                    seafood: "🐟",
                    vegetarian: "🌱",
                  };

                  return (
                    <button
                      key={category}
                      onClick={() => handleCategoryChange(category)}
                      className={`flex-shrink-0 relative overflow-hidden transition-all duration-300 transform hover:scale-105 active:scale-95 ${
                        selectedCategory === category
                          ? "bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600 text-white shadow-2xl scale-105"
                          : "bg-white text-gray-700 shadow-lg hover:shadow-xl border border-gray-100"
                      }`}
                      style={{
                        borderRadius: "16px",
                        width: "72px",
                        height: "90px",
                      }}
                      disabled={categoryItems.length === 0}
                    >
                      {/* Background pattern for selected */}
                      {selectedCategory === category && (
                        <div className="absolute inset-0 opacity-10">
                          <div className="absolute top-0 right-0 w-16 h-16 bg-white rounded-full transform translate-x-8 -translate-y-8"></div>
                          <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/20 rounded-full transform -translate-x-10 translate-y-10"></div>
                        </div>
                      )}

                      <div className="relative z-10 p-2 h-full flex flex-col items-center justify-center text-center">
                        <div
                          className={`text-2xl mb-1 transform transition-transform duration-300 ${
                            selectedCategory === category
                              ? "animate-bounce"
                              : ""
                          }`}
                        >
                          {categoryEmoji[category] || "🍽️"}
                        </div>

                        <div className="space-y-0.5">
                          <span
                            className={`block font-medium text-[10px] leading-tight ${
                              selectedCategory === category
                                ? "text-white"
                                : "text-gray-900"
                            }`}
                          >
                            {category.charAt(0).toUpperCase() +
                              category.slice(1)}
                          </span>

                          <div
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              selectedCategory === category
                                ? "bg-white/20 text-white"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {categoryItems.length}
                          </div>
                        </div>
                      </div>

                      {/* Active indicator */}
                      {selectedCategory === category && (
                        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-6 h-1 bg-white/50 rounded-full"></div>
                      )}
                    </button>
                  );
                })
              ) : (
                <div
                  className="w-full p-8 text-center bg-white rounded-3xl border border-gray-100"
                  style={{ minWidth: "280px" }}
                >
                  <Filter className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">
                    No categories available
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom gradient fade */}
        <div className="h-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
      </div>

      {/* Desktop Sidebar - Fixed width and positioning */}
      <div className="hidden lg:block w-full">
        <div className="sticky top-8 space-y-4">
          {/* Categories Card */}
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden backdrop-blur-sm">
            <div className="bg-gradient-to-r from-slate-50 via-blue-50 to-indigo-50 p-4 lg:p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5"></div>
              <h3 className="font-bold text-gray-900 flex items-center relative z-10">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 w-8 h-8 lg:w-10 lg:h-10 rounded-2xl flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                  <Filter className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
                </div>
                <span className="text-sm lg:text-base truncate">
                  Menu Categories
                </span>
              </h3>
            </div>

            <div className="p-3 lg:p-4 space-y-1 lg:space-y-2 max-h-96 overflow-y-auto scrollbar-hide">
              {categories.length > 0 ? (
                categories.map((category) => {
                  // restaurantMenu is now a flat array, not an object
                  const categoryItems = Array.isArray(restaurantMenu)
                    ? restaurantMenu.filter(
                        (item) => item.category === category
                      )
                    : [];
                  const categoryEmoji = {
                    appetizers: "🥗",
                    mains: "🍖",
                    desserts: "🍰",
                    beverages: "🥤",
                    pasta: "🍝",
                    pizza: "🍕",
                    sushi: "🍱",
                    ramen: "🍜",
                    salads: "🥙",
                    soups: "🍲",
                    seafood: "🐟",
                    vegetarian: "🌱",
                  };

                  return (
                    <button
                      key={category}
                      onClick={() => handleCategoryChange(category)}
                      className={`w-full text-left p-3 lg:p-4 rounded-2xl transition-all duration-300 relative group transform hover:scale-[1.02] ${
                        selectedCategory === category
                          ? "bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 text-blue-700 font-semibold shadow-lg border-2 border-blue-200 scale-[1.02]"
                          : "text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-slate-50 hover:shadow-md border-2 border-transparent hover:border-gray-100"
                      }`}
                      disabled={categoryItems.length === 0}
                    >
                      <div className="flex justify-between items-center min-w-0">
                        <div className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
                          <div className="text-xl lg:text-2xl transform group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                            {categoryEmoji[category] || "🍽️"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="block font-medium text-sm lg:text-base truncate">
                                {category.charAt(0).toUpperCase() +
                                  category.slice(1)}
                              </span>
                              {selectedCategory === category && (
                                <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-blue-500 animate-pulse flex-shrink-0"></div>
                              )}
                            </div>
                            <span
                              className={`text-xs ${
                                selectedCategory === category
                                  ? "text-blue-600"
                                  : "text-gray-500"
                              }`}
                            >
                              {categoryItems.length}{" "}
                              {categoryItems.length === 1 ? "item" : "items"}
                            </span>
                          </div>
                        </div>
                        <ChevronRight
                          className={`w-4 h-4 transition-all duration-300 flex-shrink-0 ${
                            selectedCategory === category
                              ? "text-blue-600 transform rotate-90"
                              : "text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1"
                          }`}
                        />
                      </div>

                      {selectedCategory === category && (
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl"></div>
                      )}
                    </button>
                  );
                })
              ) : (
                <div className="p-6 lg:p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <Filter className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-sm">
                    No categories available
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Rating Badge */}
          <div className="bg-white rounded-3xl p-4 border border-gray-100 shadow-xl">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <Star className="w-6 h-6 text-white fill-current" />
              </div>
              <div className="space-y-1">
                <p className="font-bold text-lg text-gray-900">4.8</p>
                <p className="text-xs text-gray-500">Restaurant Rating</p>
                <p className="text-xs text-amber-600 font-medium">
                  500+ reviews
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </>
  );
};

export default CategorySidebar;
